import * as Sentry from '@sentry/react';
import type { PaginationMeta } from '@sr-sdks/api-sdk-axios';
import { sonner } from '@sr/ui-library';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import { useSyncExternalStore } from 'react';
import { pushDataLayerEvent } from '../analytics/dataLayer';
import type { AnalyticsEvent } from '../analytics/events';
import downloadApi from '../api/downloadApi';
import { bypassCacheConfig } from '../axiosInstance';
import { formatCappedTotal } from '../helpers/cappedTotal';
import { ApiError } from '../httpError';
import { type CsvColumn, csvHeader, toCsvChunk } from './toCsvChunk';

const { toast } = sonner;

/**
 * Above this the download is blocked before anything is fetched. A waiting-time
 * limit, not a memory one: the APP-2436 load test kept memory flat to 1M rows.
 * Raise it once real latency and per-workspace counts are known.
 */
export const CSV_MAX_ROWS = 250_000;

/**
 * Above this the dialog warns that the download can take several minutes. The
 * cloud load test measured about 10 to 12 ms a row, so a minute is reached at
 * about 12,000 rows and 250,000 takes 20 to 25 minutes (APP-2510 D3).
 */
export const CSV_WARN_ROWS = 10_000;

const PAGE_SIZE = 1000;

/**
 * The last page an export may save, so it never saves past CSV_MAX_ROWS. A
 * list with a capped total fetches one more, to see whether rows follow.
 */
const MAX_PAGES = CSV_MAX_ROWS / PAGE_SIZE;

/** A list with a capped total ran past CSV_MAX_ROWS mid-export. */
class CsvTooManyRowsError extends Error {}

/**
 * A failed 1000-row page is retried once as two 500-row pages, in case it went
 * over the 6 MB Lambda response limit.
 */
const FALLBACK_PAGE_SIZE = 500;

/**
 * Requests in flight at once, for pages and lookups alike. Keeps one export
 * well inside the stage's shared 100 requests per second.
 */
export const CSV_CONCURRENCY = 2;

/** How long a throttled (429) request waits before its one retry. */
const THROTTLE_PAUSE_MS = 1000;

/** One export at a time per tab, so one toast id. */
const TOAST_ID = 'csv-export';

type CsvExportEvent = Extract<AnalyticsEvent, { event: 'csv_export' }>;

/** GA4 counts for an export. Never pass row data or search terms. */
export const trackCsvExport = (params: Omit<CsvExportEvent, 'event'>) =>
  pushDataLayerEvent({ ...params, event: 'csv_export' });

/**
 * For every request an export makes. The axios memory cache would keep each
 * 1000-row page alive, and a failed page is retried and then reported in the
 * toast, not on the global error page.
 */
export const CSV_EXPORT_REQUEST_CONFIG: CacheRequestConfig = {
  ...bypassCacheConfig(),
  suppressErrorModal: true,
};

/** {@link CSV_EXPORT_REQUEST_CONFIG}, cancelled with the export. */
export const csvRequestConfig = (signal: AbortSignal) => ({
  ...CSV_EXPORT_REQUEST_CONFIG,
  signal,
});

/**
 * A list page's CSV button waits until the rows on screen are for the current
 * params (not `keepPreviousData` ones) and there is at least one to export.
 */
export const isCsvExportDisabled = (
  query: { isPlaceholderData?: boolean; isSuccess: boolean },
  totalItems: number | undefined,
) => !query.isSuccess || Boolean(query.isPlaceholderData) || !totalItems;

/**
 * Only failures a second try can fix: a server error, a page over the response
 * size limit, throttling, or a dropped connection. Never another 4xx, and never
 * the 401 sign-out,
 * which the axios interceptor turns into a status-less error after resetting
 * the app — retrying that would sign the member out again for every request.
 */
const isRetryable = (error: unknown) => {
  if (!(error instanceof ApiError)) return false;

  const { code, status } = error;

  if (status === undefined) {
    return code === 'ERR_NETWORK' || code === 'ECONNABORTED';
  }

  return status >= 500 || status === 413 || status === 429;
};

/** A wait that Cancel cuts short, so a paused export still stops at once. */
const pause = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms);

    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true },
    );
  });

/**
 * Lookups the rows don't carry (e.g. client names), resolved per page. Pass
 * `signal` to its requests, so Cancel aborts them.
 */
export type CsvContextResolver<T, C> = (
  rows: T[],
  signal: AbortSignal,
) => Promise<C>;

/**
 * Optional only when the columns read no context. Otherwise a page that
 * forgets its resolver compiles, and every column reading `ctx` throws on
 * `undefined` mid-export.
 */
type ResolveContextField<T, C> = [C] extends [void]
  ? { resolveContext?: CsvContextResolver<T, C> }
  : { resolveContext: CsvContextResolver<T, C> };

/**
 * Reports the failure, not the `ApiError` itself. That carries the request
 * `params` (the search term) and the server's message, which can echo input,
 * so only its shape is reported; anything else is a code error. Sentry's
 * default request breadcrumbs still record each export URL, as they do for
 * every on-screen query.
 */
const reportCsvExportFailure = (error: unknown, fileLabel: string) => {
  const tags = { page: fileLabel, type: 'csvExport' };

  if (error instanceof ApiError) {
    Sentry.captureException(
      new Error(`CSV export request failed: ${error.endpoint ?? 'unknown'}`),
      {
        extra: { requestId: error.requestId },
        tags: {
          ...tags,
          code: error.code,
          endpoint: error.endpoint,
          status: error.status,
        },
      },
    );

    return;
  }

  Sentry.captureException(error, { tags });
};

export type CsvExportSource<T extends { _id: string }, C> = {
  /** The ticked columns, in spec order. */
  columns: CsvColumn<T, C>[];
  /**
   * Pass `signal` to the request, so Cancel aborts it. Return the list's
   * `meta` too: a larger `totalItems` than `rowCount` means rows were added
   * since Download was clicked, and the export fetches the extra pages.
   */
  fetchPage: (
    page: number,
    pageSize: number,
    signal: AbortSignal,
  ) => Promise<{
    items: T[];
    meta?: Partial<Pick<PaginationMeta, 'totalItems' | 'totalItemsCapped'>>;
  }>;
  /** The page part of the filename and the GA4 `page`: "titles". */
  fileLabel: string;
  fileName: string;
  /**
   * `rowCount` is a floor: the list stopped counting at its limit. Only the
   * toast text reads this; the paging follows each page's own `meta`.
   */
  isRowCountCapped?: boolean;
  /** Used in the toasts: "Exporting Titles". */
  label: string;
  /** The row count on screen when Download was clicked. */
  rowCount: number;
} & ResolveContextField<T, C>;

// Module state, not a provider. It outlives page navigation for free,
// and there is only ever one export per tab.
let running = false;
let cancelRunning: (() => void) | undefined;
const listeners = new Set<() => void>();

const setRunning = (value: boolean) => {
  running = value;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/** True while an export runs; the CSV buttons are disabled meanwhile. */
export const useIsCsvExportRunning = () =>
  useSyncExternalStore(subscribe, () => running);

/** A reload or closing the tab still stops the export, so ask first. */
const warnOnLeave = (event: BeforeUnloadEvent) => event.preventDefault();

/** The progress toast's Cancel. Aborts the requests in flight; saves nothing. */
export const cancelCsvExport = () => cancelRunning?.();

/**
 * Fetches every page of the list the member is looking at and saves it as one
 * CSV. Each page becomes CSV text and then its own small Blob as soon as it
 * arrives, so neither the rows nor the text stay on the JS heap (the chunked
 * build, S2 in the APP-2436 load test). Never saves the file of a failed or
 * cancelled export; see `seen` below for the one way a saved file can still
 * be a row short.
 */
export const startCsvExport = async <T extends { _id: string }, C>(
  source: CsvExportSource<T, C>,
) => {
  if (running) return;

  setRunning(true);
  window.addEventListener('beforeunload', warnOnLeave);

  const track = (
    status: CsvExportEvent['status'],
    rowCount = source.rowCount,
  ) =>
    trackCsvExport({
      column_count: source.columns.length,
      page: source.fileLabel,
      row_count: rowCount,
      status,
    });

  track('started');

  const total = formatCappedTotal(source.rowCount, source.isRowCountCapped);
  let pages = Math.ceil(source.rowCount / PAGE_SIZE);
  const parts: Blob[] = [];
  // Rows can shift between pages while the export runs; drop the repeats.
  // Known gap (APP-2510 follow-up): offset paging can also *miss* a row. A
  // title created or deleted ahead of a page that has already been fetched
  // shifts a row across that page's boundary, so it lands on no page, and the
  // file still saves as complete. Fixing it needs cursor (keyset) paging on
  // the list endpoint, or a count check that flags the file; not in A1.
  const seen = new Set<string>();
  let rows = 0;
  let next = 1;
  // Aborted by Cancel, or by the first page that fails for good, so no request
  // outlives the export.
  const controller = new AbortController();
  const { signal } = controller;
  let isCancelled = false;

  cancelRunning = () => {
    isCancelled = true;
    controller.abort();
    // Now, not in `finally`: the aborted requests take a few async hops to
    // settle, and sign-out navigates away before then.
    window.removeEventListener('beforeunload', warnOnLeave);
  };

  const showProgress = () =>
    toast.loading(
      `Exporting ${source.label}: ${formatCappedTotal(rows)} / ${total}`,
      {
        action: { label: 'Cancel', onClick: cancelCsvExport },
        duration: Infinity,
        id: TOAST_ID,
        position: 'bottom-center',
      },
    );

  /** Rethrows what a retry can't fix; waits out throttling before a retry. */
  const prepareRetry = async (error: unknown) => {
    if (signal.aborted || !isRetryable(error)) throw error;

    if (error instanceof ApiError && error.status === 429) {
      await pause(THROTTLE_PAUSE_MS, signal);
    }
  };

  /** A second try for a retryable blip, so one doesn't lose the file. */
  const retryOnce = <R>(run: () => Promise<R>) =>
    run().catch(async (error: unknown) => {
      await prepareRetry(error);

      return run();
    });

  const fetchRows = async (page: number) => {
    try {
      return await source.fetchPage(page, PAGE_SIZE, signal);
    } catch (error) {
      await prepareRetry(error);

      // Halving is for a page too big to return; a throttled one just retries.
      if (error instanceof ApiError && error.status === 429) {
        return source.fetchPage(page, PAGE_SIZE, signal);
      }

      const halves = await Promise.all([
        source.fetchPage(page * 2 - 1, FALLBACK_PAGE_SIZE, signal),
        source.fetchPage(page * 2, FALLBACK_PAGE_SIZE, signal),
      ]);

      return {
        items: halves.flatMap((half) => half.items),
        meta: halves[0].meta,
      };
    }
  };

  const worker = async () => {
    while (next <= pages && !signal.aborted) {
      const page = next++;
      const { items, meta } = await fetchRows(page);

      // A row added mid-export pushes the last row onto a page past the count
      // on screen; without this the file would be silently one row short.
      pages = Math.max(pages, Math.ceil((meta?.totalItems ?? 0) / PAGE_SIZE));

      /*
       * A capped total is a floor, so a full page may not be the last. Keep a
       * page queued past it for each worker until a short page ends the list.
       * The queue reaches one page past CSV_MAX_ROWS, so a full last page of
       * exactly that many rows still saves; a row on that extra page stops
       * the export rather than save a file that is silently cut short.
       */
      if (meta?.totalItemsCapped) {
        if (page > MAX_PAGES && items.length) throw new CsvTooManyRowsError();

        if (items.length === PAGE_SIZE) {
          pages = Math.max(
            pages,
            Math.min(page + CSV_CONCURRENCY, MAX_PAGES + 1),
          );
        }
      }

      const fresh = items.filter((item) => {
        if (seen.has(item._id)) return false;
        seen.add(item._id);

        return true;
      });
      // `ResolveContextField` only lets the resolver be missing when `C` is
      // void, so `undefined` is then the whole context.
      const { resolveContext } = source as {
        resolveContext?: CsvContextResolver<T, C>;
      };
      const ctx = resolveContext
        ? await retryOnce(() => resolveContext(fresh, signal))
        : (undefined as C);

      parts[page - 1] = new Blob([toCsvChunk(fresh, source.columns, ctx)]);
      rows += fresh.length;

      if (!signal.aborted) showProgress();
    }
  };

  // An update with the same id keeps the progress toast's endless duration and
  // Cancel action unless they are reset.
  const settled = {
    action: undefined,
    duration: 7000,
    id: TOAST_ID,
    position: 'top-center',
  } as const;

  showProgress();

  try {
    const failure = await Promise.all(
      Array.from({ length: Math.min(CSV_CONCURRENCY, pages) }, worker),
    ).then(
      () => undefined,
      (error: unknown) => {
        controller.abort();

        return { error };
      },
    );

    if (isCancelled) {
      toast.dismiss(TOAST_ID);
      track('cancelled');
    } else if (failure?.error instanceof CsvTooManyRowsError) {
      toast.error(
        `More than ${CSV_MAX_ROWS.toLocaleString()} rows. Narrow your filters and try again.`,
        settled,
      );
      // `blocked` already covers the dialog's cap and a 403. This one follows
      // `started`, so its `row_count` is the rows fetched before it stopped.
      track('blocked', rows);
    } else if (failure) {
      // No error detail on purpose: it can carry row data or search terms,
      // which are personal information.
      toast.error('Download failed, try again', settled);
      track('failed');
      reportCsvExportFailure(failure.error, source.fileLabel);
    } else {
      try {
        downloadApi.downloadFile(
          new Blob([csvHeader(source.columns), ...parts], {
            type: 'text/csv;charset=utf-8',
          }),
          source.fileName,
        );
        toast.success(`${source.label} CSV downloaded`, settled);
        track('downloaded', rows);
      } catch (error) {
        // Building or saving the file can still throw (low memory, a blocked
        // download); without this the progress toast would stay up forever.
        toast.error('Download failed, try again', settled);
        track('failed');
        reportCsvExportFailure(error, source.fileLabel);
      }
    }
  } finally {
    cancelRunning = undefined;
    window.removeEventListener('beforeunload', warnOnLeave);
    setRunning(false);
  }
};
